import { from } from 'rxjs'
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators'
import { RootEpic } from '~/Store/types'
import { errorNotification } from '~/Services'
import { GroupServices } from '~/Services/GroupServices'
import { GroupActions } from './slice'

// Get group list
const getGroupListEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(GroupActions.getGroupList.match),
    switchMap(action => {
      const { orgId, page, pageSize, filter, sort, expand } = action.payload
      const filterString = filter?.queryString || undefined
      const filterData = {
        page,
        pageSize,
        expand,
        filter: filterString,
        orderBy: sort
          ? `${sort.field} ${sort.order === 'ascend' ? 'asc' : 'desc'}`
          : undefined,
      }
      return from(GroupServices.Get.searchListGroups(orgId, filterData)).pipe(
        map(res => {
          const data = (res as any).data

          const results = Array.isArray(data) ? data : data.results || []
          const queryCount = Array.isArray(data)
            ? data.length
            : data.queryCount || results.length

          return GroupActions.getGroupListSuccess({
            results,
            queryCount,
          })
        }),
        catchError(error => {
          errorNotification(error)
          return [
            GroupActions.getGroupListFail(
              error.message || 'Failed to fetch group list',
            ),
          ]
        }),
      )
    }),
  )

// Get all group list
const getAllGroupListEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(GroupActions.getAllGroupList.match),
    switchMap(action => {
      return from(GroupServices.Get.getListGroups()).pipe(
        map(res => {
          const data = (res as any).data
          const results = Array.isArray(data) ? data : data.results || []
          return GroupActions.getAllGroupListSuccess({
            results,
            queryCount: results.length,
          })
        }),
        catchError(error => {
          errorNotification(error)
          return [
            GroupActions.getAllGroupListFail(
              error.message || 'Failed to fetch group list',
            ),
          ]
        }),
      )
    }),
  )

// Create group epic
const createGroupEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(GroupActions.createGroup.match),
    switchMap(action => {
      const { data, onSuccess, onFailure } = action.payload

      return from(GroupServices.Post.createNewGroup(data)).pipe(
        map(res => GroupActions.createGroupSuccess((res as any).data)),
        tap(action => {
          if (onSuccess) onSuccess(action.payload)
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure) onFailure(error.message || 'Failed to create group')
          return [
            GroupActions.createGroupFail(
              error.message || 'Failed to create group',
            ),
          ]
        }),
      )
    }),
  )

// Update group epic
const updateGroupEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(GroupActions.updateGroup.match),
    switchMap(action => {
      const { groupId, data, onSuccess, onFailure } = action.payload

      const updateData = [{ id: groupId, ...data }]

      return from(GroupServices.Put.updateGroupInfo(groupId, updateData)).pipe(
        map(res => {
          const responseData = (res as any).data
          const updatedGroup = Array.isArray(responseData)
            ? responseData[0]
            : responseData
          return GroupActions.updateGroupSuccess(updatedGroup)
        }),
        tap(action => {
          if (onSuccess) onSuccess(action.payload)
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure) onFailure(error.message || 'Failed to update group')
          return [
            GroupActions.updateGroupFail(
              error.message || 'Failed to update group',
            ),
          ]
        }),
      )
    }),
  )

// Delete group epic
const deleteGroupEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(GroupActions.deleteGroup.match),
    switchMap(action => {
      const { orgId, groupIds, onSuccess, onFailure } = action.payload

      return from(GroupServices.Delete.removeGroups(orgId, groupIds)).pipe(
        map(res => GroupActions.deleteGroupSuccess(groupIds)),
        tap(() => {
          if (onSuccess) onSuccess()
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure) onFailure(error.message || 'Failed to delete group')
          return [
            GroupActions.deleteGroupFail(
              error.message || 'Failed to delete group',
            ),
          ]
        }),
      )
    }),
  )

// Get group list by org id
const getGroupListByOrgIdEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(GroupActions.getGroupListByOrgId.match),
    switchMap(action => {
      const { orgId, name, status, onSuccess, onFailure } = action.payload

      return from(
        GroupServices.Get.getListGroupsByOrgId(orgId, name, status),
      ).pipe(
        map(res => {
          const data = (res as any).data
          const results = Array.isArray(data) ? data : data.results || []
          const queryCount = Array.isArray(data)
            ? data.length
            : data.queryCount || results.length

          return GroupActions.getGroupListByOrgIdSuccess({
            results,
            queryCount,
          })
        }),
        tap(action => {
          if (onSuccess) onSuccess(action.payload.results)
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure)
            onFailure(error.message || 'Failed to fetch group list')
          return [
            GroupActions.getGroupListByOrgIdFail(
              error.message || 'Failed to fetch group list',
            ),
          ]
        }),
      )
    }),
  )

// Delete group by id
const deleteGroupByIdEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(GroupActions.deleteGroupById.match),
    switchMap(action => {
      const { id, orgId, onSuccess, onFailure } = action.payload

      return from(GroupServices.Delete.removeGroupById(id, orgId)).pipe(
        map(res => GroupActions.deleteGroupByIdSuccess(id)),
        tap(() => {
          if (onSuccess) onSuccess()
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure) onFailure(error.message || 'Failed to delete group')
          return [
            GroupActions.deleteGroupByIdFail(
              error.message || 'Failed to delete group',
            ),
          ]
        }),
      )
    }),
  )

// Export all groups groups epic
const exportAllStaffsEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(GroupActions.exportAllGroups.match),
    switchMap(action => {
      const { orgId, onSuccess, onFailure } = action.payload

      return from(GroupServices.Get.exportAllGroups(orgId)).pipe(
        map(res => {
          if (onSuccess) onSuccess((res as any).data)
          return GroupActions.exportAllGroupsSuccess()
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure)
            onFailure(error.message || 'Failed to export all groups')
          return [
            GroupActions.exportAllGroupsFail(
              error.message || 'Failed to export all groups',
            ),
          ]
        }),
      )
    }),
  )

// Export groups in current page epic
const exportGroupsInCurrentPageEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(GroupActions.exportGroupsInCurrentPage.match),
    switchMap(action => {
      const { orgId, page, pageSize, filter, sort, expand, onSuccess, onFailure } =
        action.payload
      const filterString = filter?.queryString || undefined
      const filterData = {
        page,
        pageSize,
        expand,
        filter: filterString,
        orderBy: sort
          ? `${sort.field} ${sort.order === 'ascend' ? 'asc' : 'desc'}`
          : undefined,
      }

      return from(GroupServices.Get.exportCurrentPage(orgId, filterData)).pipe(
        map(res => {
          if (onSuccess) onSuccess((res as any).data)
          return GroupActions.exportGroupsInCurrentPageSuccess()
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure)
            onFailure(error.message || 'Failed to export current page groups')
          return [
            GroupActions.exportGroupsInCurrentPageFail(
              error.message || 'Failed to export current page groups',
            ),
          ]
        }),
      )
    }),
  )

// Export selected groups epic
const exportSelectedGroupsEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(GroupActions.exportSelectedGroups.match),
    switchMap(action => {
      const { orgId, groupIds, onSuccess, onFailure } = action.payload

      return from(GroupServices.Get.exportAllGroups(orgId, groupIds)).pipe(
        map(res => {
          if (onSuccess) onSuccess((res as any).data)
          return GroupActions.exportSelectedGroupsSuccess()
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure)
            onFailure(error.message || 'Failed to export selected groups')
          return [
            GroupActions.exportSelectedGroupsFail(
              error.message || 'Failed to export selected groups',
            ),
          ]
        }),
      )
    }),
  )

// Assign group to staff epic
const assignGroupToStaffEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(GroupActions.assignGroupToStaff.match),
    switchMap(action => {
      const { jobId, organizationId, userId, onSuccess, onFailure } =
        action.payload

      return from(
        GroupServices.Put.assignGroupToStaff(jobId, organizationId, userId),
      ).pipe(
        map(res => {
          if (onSuccess) onSuccess()
          return GroupActions.assignGroupToStaffSuccess()
        }),
        tap(() => {
          if (onSuccess) onSuccess()
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure)
            onFailure(error.message || 'Failed to assign group to staff')
          return [
            GroupActions.assignGroupToStaffFail(
              error.message || 'Failed to assign group to staff',
            ),
          ]
        }),
      )
    }),
  )

// Import groups epic
const importGroupsEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(GroupActions.importGroups.match),
    switchMap(action => {
      const { orgId, file, onSuccess, onFailure } = action.payload

      return from(GroupServices.Post.importGroups(orgId, file)).pipe(
        map(res => {
          if (onSuccess) onSuccess((res as any).data)
          return GroupActions.importGroupsSuccess()
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure) onFailure(error.message || 'Failed to import staffs')
          return [
            GroupActions.importGroupsFail(
              error.message || 'Failed to import staffs',
            ),
          ]
        }),
      )
    }),
  )

export const GroupEpics = [
  getGroupListEpic,
  getAllGroupListEpic,
  getGroupListByOrgIdEpic,
  createGroupEpic,
  updateGroupEpic,
  deleteGroupEpic,
  deleteGroupByIdEpic,
  exportAllStaffsEpic,
  exportGroupsInCurrentPageEpic,
  exportSelectedGroupsEpic,
  assignGroupToStaffEpic,
  importGroupsEpic,
]
